/**
 * Step 01 graphic: a mock storefront building itself, ending on a LIVE badge.
 * Ported from LaunchGraphic.dc.html. Purely decorative -- the card's heading
 * and body carry the meaning, so the whole thing is hidden from assistive tech.
 *
 * Entrance animations restart by remount: the parent changes this component's
 * key when the step becomes active, which is the React equivalent of the
 * source's show:false -> rAF -> show:true replay.
 */
const dot = {
  width: 8,
  height: 8,
  borderRadius: 999,
  background: 'var(--bw-ink)',
} as const;

const navPill = (w: number) => ({
  width: w,
  height: 7,
  borderRadius: 999,
  background: 'var(--ink-300)',
});

function ProductTile({ delay, bar }: { delay: string; bar: string }) {
  return (
    <div
      style={{
        border: '2px solid var(--bw-ink)',
        borderRadius: 8,
        overflow: 'hidden',
        animation: `hiw-rise .4s var(--ease-bounce) both ${delay}`,
      }}
    >
      <span
        style={{
          display: 'block',
          height: 32,
          background: 'var(--ink-200)',
          borderBottom: '2px solid var(--bw-ink)',
        }}
      />
      <span
        style={{
          display: 'block',
          height: 6,
          margin: '6px 6px 3px',
          borderRadius: 3,
          background: 'var(--ink-400)',
        }}
      />
      <span
        style={{
          display: 'block',
          height: 6,
          width: bar,
          margin: '0 6px 7px',
          borderRadius: 3,
          background: 'var(--ink-300)',
        }}
      />
    </div>
  );
}

export function LaunchGraphic() {
  return (
    <div
      aria-hidden="true"
      className="flex h-full w-full items-center justify-center p-2"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          border: '2px solid var(--bw-ink)',
          borderRadius: 12,
          background: '#fff',
          overflow: 'hidden',
          boxShadow: '4px 4px 0 var(--bw-ink)',
          position: 'relative',
        }}
      >
        {/* browser chrome */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 10px',
            borderBottom: '2px solid var(--bw-ink)',
            background: 'var(--surface-sunken)',
          }}
        >
          <span style={dot} />
          <span style={dot} />
          <span style={dot} />
          <span
            style={{
              marginLeft: 6,
              flex: 1,
              height: 16,
              borderRadius: 999,
              border: '2px solid var(--bw-ink)',
              background: 'var(--bw-yellow)',
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
              font: '700 8px/1 var(--font-body)',
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--bw-ink)',
              overflow: 'hidden',
            }}
          >
            yourstore.com
          </span>
        </div>

        <div
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
          }}
        >
          {/* site nav skeleton */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              animation: 'hiw-rise .42s var(--ease-bounce) both .05s',
            }}
          >
            <span
              style={{
                width: 34,
                height: 12,
                borderRadius: 3,
                background: 'var(--bw-ink)',
              }}
            />
            <span style={{ flex: 1 }} />
            <span style={navPill(26)} />
            <span style={navPill(26)} />
            <span style={navPill(18)} />
          </div>

          {/* hero block with a light sweep */}
          <div
            style={{
              position: 'relative',
              height: 76,
              border: '2px solid var(--bw-ink)',
              borderRadius: 8,
              background: 'var(--bw-yellow)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 7,
              padding: '0 14px',
              overflow: 'hidden',
              animation: 'hiw-rise .5s var(--ease-bounce) both .3s',
            }}
          >
            <span
              style={{
                width: '62%',
                height: 11,
                borderRadius: 3,
                background: 'var(--bw-ink)',
              }}
            />
            <span
              style={{
                width: '44%',
                height: 7,
                borderRadius: 3,
                background: 'rgba(0,0,0,.35)',
              }}
            />
            <span
              style={{
                width: 70,
                height: 20,
                borderRadius: 999,
                border: '2px solid var(--bw-ink)',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                font: '800 8px/1 var(--font-display)',
                letterSpacing: '.1em',
                color: 'var(--bw-ink)',
              }}
            >
              SHOP NOW
            </span>
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '34%',
                height: '100%',
                background:
                  'linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent)',
                animation: 'hiw-sweep 1.1s ease-in-out both 1.5s',
              }}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 9,
            }}
          >
            <ProductTile delay=".62s" bar="40%" />
            <ProductTile delay=".78s" bar="55%" />
            <ProductTile delay=".94s" bar="48%" />
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            right: 8,
            bottom: 10,
            padding: '6px 12px',
            border: '2px solid var(--bw-ink)',
            borderRadius: 999,
            background: 'var(--bw-yellow)',
            boxShadow: '3px 3px 0 var(--bw-ink)',
            font: '800 10px/1 var(--font-display)',
            letterSpacing: '.12em',
            color: 'var(--bw-ink)',
            animation: 'hiw-pop .5s var(--ease-bounce) both 1.6s',
          }}
        >
          LIVE
        </div>
      </div>
    </div>
  );
}
